import { ChangeEvent, FC, ReactNode, useState } from 'react';
import Highlighter from 'react-highlighter';

import { ChevronUpSmallIcon, SearchIcon } from '@/assets/icons';
import { Input } from '@/components/atoms';
import { cn } from '@/utils/tailwind.ts';

import { Button, Tag } from '../../../atoms';
import {
  Dropdown,
  DropdownMenuCheckbox,
  DropdownMenuClearSelection,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';

export type TagOption = {
  label: string;
  value: string;
  [key: string]: any;
};

type TagsDropdownProps = {
  options: TagOption[];
  selectedOptions: string[];
  triggerLabel: string;
  triggerIcon?: ReactNode;
  triggerVariant?: 'default' | 'transparent';
  className?: string;
  filterValue?: string;
  placeholder?: string;
  onClickClearSeletion?: () => void;
  onFilterChange?: (value: string) => void;
  onSelect: (value: string[]) => void;
};

export const TagsDropdown: FC<TagsDropdownProps> = ({
  options,
  selectedOptions,
  triggerLabel,
  triggerIcon,
  triggerVariant = 'default',
  className,
  filterValue,
  placeholder,
  onClickClearSeletion,
  onFilterChange,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFilterChange?.(e.target.value);
  };

  const handleSelect = (value: string) => {
    const isExist = selectedOptions.includes(value);
    onSelect(
      isExist ? selectedOptions.filter(item => item !== value) : [...selectedOptions, value]
    );
  };

  const hasSelection = selectedOptions.length > 0;

  return (
    <Dropdown onOpenChange={setIsOpen}>
      <DropdownMenuTrigger variant={triggerVariant} asChild>
        <Button variant="secondary">
          {triggerIcon}{' '}
          <span
            className={cn(
              'transition-colors',
              isOpen ? 'text-custom-warm-grey-700' : 'text-custom-warm-grey-500'
            )}
          >
            {triggerLabel}
          </span>{' '}
          {hasSelection ? (
            <Tag size="regular" color="cyan" label={selectedOptions.length.toString()} />
          ) : (
            <span className="text-custom-warm-grey-400">
              <ChevronUpSmallIcon
                className={cn('transition-transform', isOpen ? 'rotate-180' : 'rotate-0')}
              />
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={className} align="start">
        <Input
          leftIcon={<SearchIcon />}
          value={filterValue}
          onChange={handleChange}
          placeholder={placeholder}
          className="select-none"
        />
        {options.map(({ label, value }) => (
          <DropdownMenuCheckbox
            key={value}
            checked={selectedOptions.includes(value)}
            onCheckedChange={() => handleSelect(value)}
          >
            <Highlighter search={filterValue ?? ''}>{label}</Highlighter>
          </DropdownMenuCheckbox>
        ))}
        {hasSelection && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuClearSelection onClick={onClickClearSeletion}>
              Clear selection
            </DropdownMenuClearSelection>
          </>
        )}
      </DropdownMenuContent>
    </Dropdown>
  );
};
